import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { brazeDisable, brazeInit, brazeScript, setBrazeIamNavigateHandler } from 'apps/auth/analytics/braze';
import NotFound from 'apps/auth/components/NotFound';
import RedirectComponent from 'apps/auth/components/utils/RedirectComponent';
import { AUTH_SEND_MAIN_LINK, LOGIN, REGISTRATION } from 'apps/auth/constants/UrlConstants';
import { removeScript } from 'apps/auth/helpers/DOM';
import { useAuth } from 'apps/auth/hooks/selectors';
import { ConsentService } from 'apps/auth/services/ConsentService';

const AuthApp = ({ routes = [], landing = [], ...props }) => {
    const authUser = props.authUser;
    const redirectTo = props.redirectTo;
    const { ballId } = useAuth() || {};
    const navigate = useNavigate();

    const brazeInitializedRef = useRef(false);
    // Route IAM clicks to internal destinations through the SPA router instead of a full reload.
    useEffect(() => {
        if (!__isBrowser__) return undefined;
        setBrazeIamNavigateHandler((path) => navigate(path));
        return () => setBrazeIamNavigateHandler(null);
    }, [navigate]);

    useEffect(() => {
        if (!__isBrowser__) return;

        const tryInitBraze = () => {
            // wait until OneTrust is available
            if (!ConsentService.hasLoaded()) return;
            const { braze } = ConsentService.getConsentState();
            if (braze && ballId && !brazeInitializedRef.current) {
                brazeInit();
                brazeInitializedRef.current = true;
            } else if (!braze) {
                // disable Braze if it was previously initialized
                if (brazeInitializedRef.current && window.braze) {
                    brazeDisable();
                    brazeInitializedRef.current = false;
                }
            }
        };

        tryInitBraze();

        return () => {
            removeScript(brazeScript);
        };
    }, [ballId]);

    return (
        <Routes>
            {routes.map(({ path, exact, component: Component, stateOptions, renderOptions, ...rest }) => {
                const isRedirect =
                    (authUser && (path === LOGIN || path === REGISTRATION)) || (!authUser && rest.isAuthZone) || (redirectTo && redirectTo !== path);
                const redirectUrl = redirectTo ? redirectTo : isRedirect && authUser ? AUTH_SEND_MAIN_LINK : LOGIN;
                return (
                    <Route
                        key={path}
                        path={path}
                        exact={exact}
                        element={
                            isRedirect ? (
                                <Navigate to={redirectUrl} />
                            ) : (
                                <Component reloadData={isRedirect} renderOptions={renderOptions} stateOptions={stateOptions} data={props.data} />
                            )
                        }
                    />
                );
            })}
            {landing.map(({ path, exact }) => {
                if (Array.isArray(path)) {
                    return path.map((p) => <Route key={p} path={p} exact={exact} element={<RedirectComponent key={p} />} />);
                }
                return <Route key={path} path={path} exact={exact} element={<RedirectComponent />} />;
            })}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default AuthApp;
